/**
 * Root Component der Chatbot-Anwendung
 *
 * Verwaltet Analytics-Consent-Handling und Integration mit der Portal-Umgebung.
 * OIDC-Authentifizierung erfolgt in APP_INITIALIZER (siehe app.config.ts).
 *
 * @class AppComponent
 */
import { Component, DestroyRef, inject, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { MatomoTracker } from 'ngx-matomo-client';
import { IntentActionService } from './services/intent-action.service';
import { ContextInfoService, WellKnownJustFarmingProperties } from './portal/context-info.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _intentActionService = inject(IntentActionService);
  private readonly _contextInfoService = inject(ContextInfoService);
  private readonly _tracker = inject(MatomoTracker);

  /**
   * Angular Lifecycle Hook für Component-Initialisierung
   *
   * Abonniert Context Info Änderungen für Analytics Consent Management.
   * Alle Subscriptions werden automatisch bei Component-Zerstörung aufgeräumt.
   *
   * **Hinweis**: OIDC checkAuth() erfolgt in APP_INITIALIZER (app.config.ts)
   * VOR dem Routing, um Race Conditions zu vermeiden.
   */
  ngOnInit(): void {
    // NOTE: checkAuth() wurde nach APP_INITIALIZER verschoben (siehe app.config.ts)
    // Dies ist CRITICAL um Race Conditions zu vermeiden - checkAuth() muss VOR
    // dem Routing laufen, sonst kann AuthGuard nicht korrekt OAuth Callbacks verarbeiten.

    // Context info subscription für Analytics Consent Management
    this._contextInfoService.contextInfo$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (contextInfo) => {
        let analytics = contextInfo?.properties.get(WellKnownJustFarmingProperties.ANALYTICS);
        if (analytics === true) {
          this._tracker.setConsentGiven();
        } else {
          this._tracker.forgetConsentGiven();
        }
      },
    });
  }
}
